import { useEffect, useRef, useState } from "react";
import { Container, Typography, Button, Stack, Box, Divider, useTheme, useMediaQuery } from "@mui/material";
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { Link } from "react-router-dom";
import SectionHeader from "../components/SectionHeader";

const s3BaseUrl = "https://djinni-assets.s3.us-east-1.amazonaws.com/how-clean/";
const blocks = [
  {
    image: s3BaseUrl + "start.jpg",
    title: "Inspect Property for Damages",
    desc: "Before we start cleaning, we inspect the property for any visible damage or missing items and report them immediately to protect your investment.",
    button: null,
  },
  {
    image: s3BaseUrl + "checklist.jpg",
    title: "Thorough Cleaning with Checklist",
    desc: "Our cleaners follow a detailed Airbnb-specific checklist to ensure every area meets guest-ready standards, including bathrooms, kitchens, and high-touch surfaces.",
    button: (
      <Button component={Link} to="/checklist" variant="contained" color="secondary" size="large">
        Customize Your Checklist
      </Button>
    ),
  },
  {
    image: s3BaseUrl + "photo.jpg",
    title: "Take Timestamped Photos After Cleaning",
    desc: "After the cleaning is complete, we take timestamped photos of each room so you can see the results and have proof of condition before the next guest arrives.",
    button: null,
  },
  {
    image: s3BaseUrl + "supplies.jpg",
    title: "Restock Essentials",
    desc: "We replenish guest supplies such as toiletries, paper towels, and cleaning items, ensuring your unit is ready for the next stay.",
    button: null,
  },
  {
    image: s3BaseUrl + "end.jpg",
    title: "Final Walkthrough",
    desc: "We perform a final check of the space, adjust lighting, secure windows and doors, and set the thermostat to ensure everything is perfect.",
    button: null,
  },
];

interface HowWeCleanProps {
  bgcolor?: string;
}

function HowWeClean({ bgcolor }: HowWeCleanProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const firstCard = container.children[0] as HTMLElement | null;
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = 16;
        const scrollPosition = container.scrollLeft;
        const index = Math.round(scrollPosition / (cardWidth + gap));
        setCurrentIndex(index);
      }
    }
  };

  const scrollToCard = (direction: "prev" | "next") => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const firstCard = container.children[0] as HTMLElement | null;
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = 16;
        const scrollAmount = cardWidth + gap;
        let newIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;
        newIndex = Math.max(0, Math.min(blocks.length - 1, newIndex));

        container.scrollTo({
          left: newIndex * scrollAmount,
          behavior: "smooth",
        });
        setCurrentIndex(newIndex);
      }
    }
  };

  return (
    <Box sx={{ width: "100%", bgcolor }}>
      <Container
        component="section"
        maxWidth="md" // Adds space around the Hero section
        sx={{
          py: 4, // Vertical padding
          px: 4, // Horizontal padding
        }}
      >
        <SectionHeader title="How We Clean" />

        {isMobile ? (
          <Box sx={{ position: "relative", mb: 6 }}>
            <Box
              ref={scrollContainerRef}
              onScroll={handleScroll}
              sx={{
                display: "flex",
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                scrollBehavior: "smooth",
                gap: 2,
                pb: 2,
                mx: -0.5,
                px: 0.5,
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }}
            >
              {blocks.map((block, index) => (
                <Box
                  key={index}
                  sx={{
                    minWidth: "calc(100% - 64px)",
                    maxWidth: "calc(100% - 64px)",
                    scrollSnapAlign: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                      bgcolor: "background.paper",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      height: "100%",
                      minHeight: "520px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        aspectRatio: "1 / 1",
                        height: "200px",
                        backgroundImage: `url('${block.image}')`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        borderRadius: "12px",
                        mb: 3,
                        mx: "auto",
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {block.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" gutterBottom>
                        {block.desc}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "left", mt: 2 }}>{block.button}</Box>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5, mt: 2 }}>
              <Button
                onClick={() => scrollToCard("prev")}
                disabled={currentIndex === 0}
                sx={{ minWidth: 40, height: 40, borderRadius: "50%", p: 0 }}
              >
                <ChevronLeftIcon />
              </Button>

              <Stack direction="row" spacing={1}>
                {blocks.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: currentIndex === index ? "primary.main" : "grey.300",
                      transition: "all 0.3s ease",
                    }}
                  />
                ))}
              </Stack>

              <Button
                onClick={() => scrollToCard("next")}
                disabled={currentIndex === blocks.length - 1}
                sx={{ minWidth: 40, height: 40, borderRadius: "50%", p: 0 }}
              >
                <ChevronRightIcon />
              </Button>
            </Box>
          </Box>
        ) : (
          blocks.map((block, index) => {
            const isEven = index % 2 === 0;

            return (
              <Box key={index} sx={{ mb: 6 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  alignItems="center"
                  flexDirection={{ xs: "column", md: isEven ? "row" : "row-reverse" }}
                >
                  {/* Text Section */}
                  <Box flex={1} sx={{ textAlign: "left" }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {block.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      {block.desc}
                    </Typography>
                    <Box sx={{ textAlign: "left", mt: 2 }}>{block.button}</Box>
                  </Box>

                  <Box width={"30px"} height={"25px"}> </Box>

                  {/* Image Section */}
                  <Box
                    sx={{
                      aspectRatio: "1 / 1",
                      height: "200px",
                      backgroundImage: `url('${block.image}')`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      borderRadius: "8px",
                      mx: "auto",
                    }}
                  />
                </Stack>

                {/* Divider (except after last item) */}
                {index < blocks.length - 1 && (
                  <Divider
                    sx={{
                      width: "80%",
                      backgroundColor: "background.paper",
                      my: 4,
                      mx: "auto",
                    }}
                  />
                )}
              </Box>
            );
          })
        )}
      </Container>
    </Box>
  );
}

export default HowWeClean;
